import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

type NestingNode = {
	attrs?: {
		parameters?: {
			nestedContent?: unknown;
		};
	};
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getNestedContentForTraversal = (node: NestingNode): ADFEntity | undefined => {
	const nestedContent = node.attrs?.parameters?.nestedContent;
	return isObject(nestedContent) && Array.isArray(nestedContent.content)
		? (nestedContent as ADFEntity)
		: undefined;
};

/**
 * Extends traverse from @atlaskit/adf-utils to also walk into nestedContent
 * found in extension parameters (e.g. legacy-content, column macro).
 *  "content": [
        {
          "type": "extension",
          "attrs": {
            "extensionKey": "legacy-content",
            "extensionType": "com.atlassian.confluence.migration",
            "parameters": {
              "reason": "A column macro in a rich body macro can't be created or edited in the new editor.",
              "nestedContent": {
                "type": "doc",
                "content": [
                  {
                    "type": "bodiedExtension",
                    "attrs": {
                      "extensionType": "com.atlassian.confluence.macro.core",
                      "extensionKey": "column"
                  }
                ]
              }
            }
          }
        }
      ]
    }
  }
]
 */
export function traverseWithNestedContentAndDepth(
	adf: ADFEntity,
	visitor: (node: ADFEntity, depth: number) => void,
	depthOffset: number = 0,
): void {
	const activeDocuments = new WeakSet<object>();
	const walk = (document: ADFEntity, currentDepthOffset: number): void => {
		if (activeDocuments.has(document)) {
			return;
		}

		activeDocuments.add(document);
		try {
			traverse(document, {
				any: (node, _parent, _index, depth) => {
					visitor(node, currentDepthOffset + depth);

					const nestedContent = getNestedContentForTraversal(node);
					if (nestedContent) {
						walk(nestedContent, currentDepthOffset + depth + 1);
					}

					return node;
				},
			});
		} finally {
			activeDocuments.delete(document);
		}
	};

	walk(adf, depthOffset);
}
